<template>
  <aw-spinner />
</template>

<script setup lang="ts">
  import { onMounted } from 'vue';
  import { navigateTo, useAsyncData, useNuxtApp, useRoute } from 'nuxt/app';
  import { fetchKey } from '~~/common/utils/awRunLegacyAsyncData';
  import { getLocaleCodeForUrl } from '~~/common/utils/localeCode';
  import type { Schema } from '~~/common/types/api';
  import { useOrderStore } from '~~/common/stores/order';
  import { useCheckoutStore } from '~~/common/stores/checkout';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';

  defineOptions({
    name: 'PaymentRedirect',
  });

  const { $i18n, $api, $logger, $localePath } = useNuxtApp();
  const route = useRoute();
  const orderStore = useOrderStore();
  const checkoutStore = useCheckoutStore();

  const { data } = await useAsyncData(
    fetchKey(),
    async () => {
      try {
        const result = await $api.$post<Schema<'PaymentReturnModel.PaymentReturnOutputModel'>>('/payment_return', {
          transactionNumber: route.query.transactionNumber,
        });
        const successOrderCart = await $api.$get<Schema<'CartModel'>>(`/orders/${result.orderIdent}/cart`);
        const paymentData = await $api.$get<Schema<'PaymentTransactionModel'>[]>(`/orders/${result.orderIdent}/payment_transactions`);

        const checkoutType = successOrderCart.splits[0]?.checkoutType;
        const cartData = successOrderCart.splits[0];
        orderStore.setPaymentData({
          paymentData,
          checkoutType,
        });

        await orderStore.fetchOrder({
          id: 'current',
          fetchNonfoodCheckoutIfNFCartNotEmpty: false,
          fetchFoodCheckout: false,
        });

        await checkoutStore.fetchCheckout({
          type: checkoutType,
          canStartSecondAction: true,
          canRunSimultaneous: true,
        });

        const isCardInfoShown = Boolean(parseInt(String(route.query.isCardInfoShown), 10));
        if (checkoutType === 'FOOD') {
          orderStore.setCompletedFoodOrder({
            ...cartData,
            orderIdent: result.orderIdent,
            showCardInfo: isCardInfoShown,
          });
        } else if (checkoutType === 'NONFOOD') {
          orderStore.setCompletedNonFoodOrder({
            ...cartData,
            orderIdent: result.orderIdent,
            showCardInfo: isCardInfoShown,
          });
        }
        return {
          orderIdent: result.orderIdent,
          checkoutType: checkoutType?.toLowerCase() ?? null,
          isCardInfoShown,
        };
      } catch (e) {
        $logger.error(e);
        return null;
      }
    },
    {
      default: () => null,
    },
  );

  if (!data.value?.orderIdent) {
    await navigateTo(`/${getLocaleCodeForUrl({ $i18n })}`);
  }

  onMounted(() => {
    const orderIdent = data.value?.orderIdent;
    if (orderIdent) {
      navigateTo({
        path: $localePath(`/shop/checkout-success/${orderIdent}`),
        query: {
          orderIdent,
          ...(orderStore.editedOrder ? { edited: 'true' } : {}),
        },
      });
    }
  });
</script>
